<template>
  <div class="w-full flex flex-col gap-5 items-center mx-auto min-w-0" style="max-width: 1300px;">
    <div class="w-full flex items-center gap-5 min-w-0">
      <div class="flex-grow flex flex-col gap-4">
        <div class="relative flex flex-col bg-neutral-25 p-1 z-10" style="border-radius: 20px;">
          <!-- CREDIT USAGE -->
          <div class="w-full flex flex-col gap-1 px-3 pt-3 pb-4">
            <BaseText type="body" size="sm" class="text-text-normal">
              Credits Used
            </BaseText>
            <div class="flex items-center gap-2">
              <div v-if="loadingUsageHistory || loadingApiKey" class="skeleton muted w-28 h-6 rounded-md" />
              <div v-else class="flex items-center flex-wrap gap-2 slow-fade-in">
                <img src="../../assets/icons/api-token.png" class="w-6 h-6">
                <BaseText type="heading" size="lg" class="text-text-muted">
                  {{ creditsSpent.toLocaleString() }}/{{ totalCredits.toLocaleString() }}
                </BaseText>
                <BaseText type="body" size="sm" class="text-text-muted px-2 py-0.5 rounded-lg bg-neutral-50 whitespace-nowrap">
                  1 credit per ad pulled.
                </BaseText>
                <!-- Refresh Date -->
                <BaseText v-if="formattedRefreshDate" type="body" size="sm" class="px-2 py-0.5 rounded-lg bg-neutral-50 text-text-muted whitespace-nowrap">
                  Refresh on {{ formattedRefreshDate }}
                </BaseText>
              </div>
              <!-- Get more credits button -->
              <button class="ml-auto flex items-center gap-1.5 px-2 py-1.5 self-end rounded-md transition-colors duration-100 hover:bg-neutral-50 whitespace-nowrap"
              @click="$emit('navigate-to-credits')">
                <BaseText type="label" size="sm" class="text-text-muted">
                  Get more credits
                </BaseText>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M8.33325 13.3334L11.0773 10.5893C11.4028 10.2639 11.4028 9.73626 11.0773 9.41083L8.33325 6.66675" stroke="#5E6678" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
          <!-- USAGE GRAPH -->
          <div class="usage-graph-container w-full flex flex-col bg-white px-3 pb-3 pt-2" style="height: 312px;">
            <!-- Table Header / Graph Selector -->
            <div class="w-full flex items-center justify-between pb-1">
              <div class="flex items-center gap-1 p-1.5">
                <CalendarIcon class="text-icon-normal" />
                <BaseText v-if="usageGraphType === 'bar'" type="label" size="sm" class="text-text-normal slow-fade-in" key="monthly-usage">
                  Monthly Usage
                </BaseText>
                <BaseText v-else type="label" size="sm" class="text-text-normal slow-fade-in" key="daily-usage">
                  Daily Usage
                </BaseText>
              </div>
              <!-- Graph Type Selector -->
              <div class="flex items-center gap-0.5 p-0.5 rounded-md bg-neutral-10">
                <button class="graph-selector-btn" :class="{ 'active': usageGraphType === 'bar' }" @click="usageGraphType = 'bar'">
                  <BarGraphIcon class="text-icon-normal" />
                </button>
                <button class="graph-selector-btn" :class="{ 'active': usageGraphType === 'line' }" @click="usageGraphType = 'line'">
                  <LineGraphIcon class="text-icon-normal" />
                </button>
              </div>
            </div>
            <div class="relative flex-grow">
              <!-- Loading Overlay -->
              <div v-if="loadingUsageHistory || loadingApiKey" class="absolute top-0 left-0 w-full h-full flex flex-col items-center justify-center">
                <BaseLoadingLogo noPadding :margin="0" />
                <div v-if="showLoadingKeyString" class="loading-text-enter flex flex-col items-center text-center gap-1 overflow-hidden flex-nowrap">
                  <BaseText type="label" size="sm" class="text-text-muted opacity-80 pt-4">
                    Hang tight...
                  </BaseText>
                  <BaseText type="body" size="sm" class="text-text-normal opacity-80">
                    We're getting everything ready for you.
                  </BaseText>
                </div>
              </div>
              <ApiUsageBarGraph 
                v-else-if="usageGraphType === 'bar'" 
                :usage-timestamps="monthlyUsageTimestamps" 
                :max-monthly-credits="totalCredits"
                class="slow-fade-in" 
              />
              <ApiUsageLineGraph 
                v-else-if="usageGraphType === 'line'"
                :usage-timestamps="dailyUsageTimestamps"
                class="slow-fade-in"
              />
            </div>
          </div>
        </div>
        <!-- DOCUMENTATION LINKS -->
        <div class="w-full grid grid-cols-3 gap-3">
          <button v-for="linkData in documentationLinks" :key="linkData.url" @click="linkData.action()"
          class="w-full flex flex-col p-3 rounded-2xl text-left border border-border-normal transition-colors duration-100 hover:border-border-hover">
            <component :is="linkData.icon" class="text-icon-normal" />
            <BaseText type="label" size="sm" class="text-text-muted mt-2">
              {{ linkData.label }}
            </BaseText>
            <BaseText type="body" size="sm" class="text-text-normal mt-1">
              {{ linkData.description }}
            </BaseText>
          </button>
        </div>
      </div>
      <!-- API KEY -->
      <div class="flex flex-col items-center gap-1 p-1 border border-border-normal flex-shrink-0" style="width: 312px; border-radius: 20px;">
        <div class="api-key-card flex flex-col items-center flex-shrink-0" style="height: 305px;">
          <KeyCardDecoration />
          <div class="flex flex-col gap-1 p-2 text-center" style="margin-top: 38px;">
            <BaseText type="label" size="md" class="text-white">
              API Key
            </BaseText>
            <BaseText type="body" size="sm" class="text-neutral-alpha-650">
              Start pulling competitor<br/>advertising data immediately.
            </BaseText>
          </div>
          <!-- Loading skeleton -->
          <div v-if="loadingApiKey" class="rounded-lg skeleton dark mt-6 border border-neutral-alpha-300" style="width: 224px; height: 38px;" />
          <!-- Key display -->
          <div v-if="!loadingApiKey && apiKeyData?.api_key" style="width: 224px; height: 38px;"
          class="px-4 py-2 rounded-lg bg-neutral-alpha-50 border border-neutral-alpha-300 mt-6 text-center">
            <BaseText type="label" size="sm" class="text-white whitespace-nowrap fade-in">
              {{ apiKeyDisplay }}
            </BaseText>
          </div>
          <!-- Copy key button -->
          <button v-if="!loadingApiKey && apiKeyData?.api_key" class="relative group p-2 mt-2 rounded-lg transition-colors duration-100 hover:bg-neutral-alpha-25 z-10 fade-in"
          style="width: 106px; height: 36px;" :disabled="keyCopySuccess" v-clipboard:copy="apiKeyData.api_key.api_key" v-clipboard:success="handleApiKeyCopy">
            <transition-group>
              <div v-if="keyCopySuccess" class="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 flex items-center gap-1.5" key="success-copy-button">
                <CheckmarkIcon class="text-neutral-alpha-650 transition-colors duration-100 group-hover:text-neutral-alpha-800" />
                <BaseText type="label" size="sm" class="text-neutral-alpha-700 transition-colors duration-100 group-hover:text-neutral-alpha-800">
                  Copied
                </BaseText>
              </div>
              <div v-else class="flex items-center gap-1.5" key="base-copy-button">
                <CopyIcon class="text-neutral-alpha-650 transition-colors duration-100 group-hover:text-neutral-alpha-800" />
                <BaseText type="label" size="sm" class="text-neutral-alpha-800 transition-colors duration-100 group-hover:text-neutral-alpha-900">
                  Copy Key
                </BaseText>
              </div>
            </transition-group>
          </button>
          <!-- Refresh Key Button -->
          <transition>
            <div v-if="!loadingApiKey" class="absolute bottom-4 left-1/2 transform -translate-x-1/2">
              <BaseTooltipV2 :tooltip-enabled="!canRefreshApiKey" text="Only the team admin can refresh an API key" :offset="2">
                <button class="group flex items-center gap-1.5 p-2 rounded-lg transition-colors duration-100 hover:bg-neutral-alpha-25"
                :class="{ 'opacity-50 cursor-default' : !canRefreshApiKey }"
                :disabled="!canRefreshApiKey" @click="showRefreshApiKeyModal = true">
                  <RefreshIcon class="text-neutral-alpha-650 transition-colors duration-100 group-hover:text-neutral-alpha-800" />
                  <BaseText type="label" size="sm" class="text-neutral-alpha-800 transition-colors duration-100 group-hover:text-neutral-alpha-900">
                    Refresh
                  </BaseText>
                </button>
              </BaseTooltipV2>
            </div>
          </transition>
        </div>
        <!-- CONNECT MCP -->
        <div class="flex flex-col px-2 pb-2 pt-2.5 gap-4">
          <div class="w-full flex justify-center">
            <MCPDecoration />
          </div>
          <BaseText type="body" size="sm" class="text-text-normal text-center" style="max-width: 288px;">
            Quick-connect your Foreplay account via MCP to Claude, Chat and more.
          </BaseText>
          <button class="secondary-btn flex items-center justify-center gap-1 p-1.5 rounded-md"
          @click="showConnectMCPModal = true">
            <ConnectMCPIcon class="text-icon-normal" />
            <BaseText type="label" size="sm" class="text-text-muted">
              Connect MCP
            </BaseText>
          </button>
        </div>
      </div>
    </div>
    <RefreshApiKeyModal 
      v-if="showRefreshApiKeyModal"
      @refresh-key="refreshApiKey"
      @close="showRefreshApiKeyModal = false"
    />
    <ConnectMCPModal 
      v-if="showConnectMCPModal"
      @close="showConnectMCPModal = false"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import firebase from '@/api/config/FirebaseConfig'
import moment from 'moment'
import ForeplayAPI from '@/api/foreplayServer'

// Components
import ApiUsageBarGraph from './ApiUsageBarGraph.vue'
import ApiUsageLineGraph from './ApiUsageLineGraph.vue'
import RefreshApiKeyModal from './RefreshApiKeyModal.vue'
import ConnectMCPModal from './mcp/ConnectMCPModal.vue'

// Icons
import KeyCardDecoration from '../globals/Icons/ApiIcons/KeyCardDecoration.vue'
import MCPDecoration from '../globals/Icons/ApiIcons/MCPDecoration.vue'
import CopyIcon from '../globals/Icons/CopyIconStatic.vue'
import CheckmarkIcon from '../globals/Icons/NewCheckmarkIcon.vue'
import BarGraphIcon from '../globals/Icons/LensIcons/graphs/BarGraphIcon.vue'
import LineGraphIcon from '../globals/Icons/LensIcons/graphs/LineGraphIcon.vue'
import CalendarIcon from '../globals/Icons/CalendarIcon.vue'
import RefreshIcon from '../globals/Icons/LensIcons/RefreshIcon.vue'
import ConnectMCPIcon from '../globals/Icons/ApiIcons/ConnectMCPIcon.vue'

import DocsGPTIcon from '../globals/Icons/ApiIcons/DocsGPTIcon.vue'
import TechnicalDocsIcon from '../globals/Icons/ApiIcons/TechnicalDocsIcon.vue'
import PlatformDocsIcon from '../globals/Icons/ApiIcons/PlatformDocsIcon.vue'

export default {
  name: 'ApiOverview',
  components: {
    ApiUsageBarGraph,
    ApiUsageLineGraph,
    RefreshApiKeyModal,
    ConnectMCPModal,
    KeyCardDecoration,
    MCPDecoration,
    CopyIcon,
    CheckmarkIcon,
    BarGraphIcon,
    LineGraphIcon,
    CalendarIcon,
    RefreshIcon,
    ConnectMCPIcon
  },
  data() {
    return {
      // Data
      apiKeyData: null,
      usageStatistics: null,
      usageTimestamps: null,
      monthlyUsageTimestamps: null,
      dailyUsageTimestamps: null,

      // UI
      usageGraphType: 'bar',
      showLoadingKeyString: false,
      showRefreshApiKeyModal: false,
      showConnectMCPModal: false,
      keyCopySuccess: false,
      loadingApiKey: true,
      loadingUsageHistory: true,
      documentationLinks: [
        {
          label: 'Docs GPT',
          description: 'Chat with the Foreplay API docs.',
          icon: DocsGPTIcon,
          action: () => { window.open('https://chatgpt.com/g/g-68b06c9529548191b5d25556e66a272a-foreplay-data-api-docs', '_blank') }
        },
        {
          label: 'Technical Docs',
          description: 'Full API endpoint documentation.',
          icon: TechnicalDocsIcon,
          action: () => { window.open('https://docs.foreplay.co/', '_blank') }
        },
        {
          label: 'Platform Docs',
          description: 'Getting started with the API.',
          icon: PlatformDocsIcon,
          action: () => { window.open('https://help.foreplay.co/collections/6711504-api', '_blank') }
        }
      ]
    }
  },
  async mounted () {
    await Promise.all([
      this.fetchApiKey(),
      this.fetchUsageHistory()
    ])
  },
  computed: {
    ...mapGetters('AuthModule', ['getUser', 'getTeam', 'getApiSubscription']),
    apiKeyDisplay() {
      const apiKey = this.apiKeyData?.api_key?.api_key
      if (!apiKey) return ''
      return apiKey.slice(0, 4) + '****************' + apiKey.slice(-4)
    },
    creditsSpent () {
      const customerData = this.apiKeyData?.active_customer
      const remainingCredits = customerData?.remaining_credits
      if (remainingCredits !== 0 && (!customerData?.remaining_credits || !customerData?.total_credits)) return 0
      return customerData.total_credits - customerData.remaining_credits
    },
    totalCredits () {
      return this.apiKeyData?.active_customer?.total_credits ?? 10000
    },
    creditRefreshDate () {
      const endDate = this.getApiSubscription?.currentPeriodEnd
      if (!endDate) return null
      return new Date(endDate * 1000)
    },
    formattedRefreshDate () {
      if (!this.creditRefreshDate) return null
      return moment(this.creditRefreshDate).format('MMM D, YYYY')
    },
    isTeamAdmin () {
      return this.getTeam?.created_by === firebase.auth().currentUser.uid
    },
    canRefreshApiKey () {
      return !this.getTeam || this.isTeamAdmin
    }
  },
  methods: {
    async intervalPoll (fetchFunction, options = {}) {
      const {
        maxAttempts = 60,
        intervalMs = 1500,
        onMaxAttemptsReached = null
      } = options

      let attempts = 0

      return new Promise((resolve) => {
        const checkData = async () => {
          attempts++
          if (attempts === 7) {
            this.showLoadingKeyString = true
          }
          try {
            const result = await fetchFunction()
            if (result.success) return true // Stop the interval

            throw new Error(result.error || 'Failed to fetch data')
          } catch (error) {
            console.error(`Fetch attempt ${attempts} failed:`, error)
          }

          // Stop attempts if we've reached the maximum
          if (attempts >= maxAttempts) {
            if (onMaxAttemptsReached) onMaxAttemptsReached()
            return true // Stop the interval
          }
          return false // Continue the interval
        }

        // First attempt
        checkData().then(success => {
          if (success) {
            resolve()
            return
          }
          // If we don't get the data immediately, start the interval
          const intervalId = setInterval(async () => {
            const shouldStop = await checkData()
            if (shouldStop) {
              clearInterval(intervalId)
              resolve()
            }
          }, intervalMs)
        })
      })
    },
    async fetchApiKey () {
      return this.intervalPoll(async () => {
        try {
          const { data } = await ForeplayAPI.PublicAPI.getAPIKey()
          if (data?.api_key?.api_key?.length) {
            this.apiKeyData = data
            this.loadingApiKey = false
            return { success: true }
          }
          return { success: false, error: 'No API key found' }
        } catch (error) {
          return { success: false, error: error.message }
        }
      }, {
        maxAttempts: 5,
        intervalMs: 1500,
        onMaxAttemptsReached: () => {
          this.loadingApiKey = false
          this.$showAlert({
            type: 'error',
            message: 'We\'re still generating your API key. Please try again later.'
          })
          this.$router.push({ name: 'DiscoveryView' })
        }
      })
    },
    async refreshApiKey () {
      this.loadingApiKey = true
      try {
        const newKey = await ForeplayAPI.PublicAPI.refreshAPIKey()
        const existingKeyData = this.apiKeyData?.api_key
        if (existingKeyData) {
          this.apiKeyData = { ...this.apiKeyData, api_key: { ...existingKeyData, api_key: newKey } }
        } else {
          await this.fetchApiKey()
        }
        this.$showAlert({
          type: 'success',
          message: 'API key successfully refreshed'
        })
      } catch (error) {
        this.$showAlert({
          type: 'error',
          message: 'Failed to refresh API key. See console for details.'
        })
        console.error('Failed to refresh API key:', error)
      } finally {
        this.loadingApiKey = false
      }
    },
    async fetchUsageHistory () {
      const monthlyPagination = { limit: 12 }
      const monthlyParameters = {
        date_aggregation: 'month',
        order_direction: 'newest',
        start_date: moment().subtract(11, 'month').startOf('month').format('YYYY-MM-DDTHH:mm:ss'),
        end_date: moment().endOf('month').format('YYYY-MM-DDTHH:mm:ss')
      }

      const dailyPagination = { limit: 31 }
      const dailyParameters = {
        date_aggregation: 'day',
        order_direction: 'newest',
        start_date: moment().subtract(30, 'day').startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
        end_date: moment().endOf('day').format('YYYY-MM-DDTHH:mm:ss')
      }

      return this.intervalPoll(async () => {
        try {
          const [monthlyResponse, dailyResponse] = await Promise.all([
            ForeplayAPI.PublicAPI.getUsageHistory(monthlyPagination, monthlyParameters, {}),
            ForeplayAPI.PublicAPI.getUsageHistory(dailyPagination, dailyParameters, {})
          ])
          if (
            monthlyResponse?.data?.statistics 
            && monthlyResponse?.data?.distribution 
            && dailyResponse?.data?.statistics 
            && dailyResponse?.data?.distribution
          ) {
            this.monthlyUsageTimestamps = monthlyResponse?.data?.distribution
            this.dailyUsageTimestamps = dailyResponse?.data?.distribution
            this.loadingUsageHistory = false
            return { success: true }
          }
          return { success: false, error: 'No usage history found' }
        } catch (error) {
          return { success: false, error: error.message }
        }
      }, {
        maxAttempts: 5,
        intervalMs: 1500,
        onMaxAttemptsReached: () => {
          this.loadingUsageHistory = false
          this.$showAlert({
            type: 'error',
            message: 'Failed to fetch usage history. See console for details.'
          })
        }
      })
    },
    handleApiKeyCopy() {
      this.keyCopySuccess = true
      setTimeout(() => { this.keyCopySuccess = false }, 2000)
    }
  }
}
</script>

<style scoped>
.usage-graph-container {
  border-radius: 16px;
  box-shadow: 0px 1.5px 1.5px -0.75px rgba(6, 7, 16, 0.08), 0px 0px 0.25px 0.75px rgba(6, 7, 16, 0.04);
}
.api-key-card {
  position: relative;
  overflow: hidden;
  height: 100%;
  width: 302px;
  border-radius: 16px;
  background-color: #060710;
}
.api-key-card::after {
  content: '';
  position: absolute;
  top: -32px;
  bottom: -32px;
  left: -32px;
  right: -32px;
  pointer-events: none;
  background: linear-gradient(
    144deg, 
    rgba(255, 255, 255, 0.16) 9.64%, 
    transparent 20.99%, 
    rgba(255, 255, 255, 0.135) 55.68%, 
    rgba(255, 255, 255, 0.135) 61.85%, 
    transparent 100%
  );
}

.graph-selector-btn {
  width: 28px;
  height: 28px;
  padding: 4px;
  border-radius: 4px;
  background-color: transparent;
  box-shadow: none;
  transition: background-color 100ms ease-in-out, box-shadow 100ms ease-in-out;
}
.graph-selector-btn:hover {
  background-color: #F6F8FA;
}
.graph-selector-btn.active, .graph-selector-btn.active:hover {
  background-color: white;
  box-shadow: 0 8px 8px -4px rgba(28, 36, 53, 0.04), 0 2px 2px -1px rgba(29, 35, 53, 0.04), 0 0 1.5px -0.25px rgba(29, 35, 53, 0.16);
}

.secondary-btn {
  box-shadow: 0 1px 2px 0 rgba(4, 26, 75, 0.13), 0 0 0 1px rgba(0, 56, 108, 0.08);
  background-color: transparent;
  transition: box-shadow 100ms ease-in-out;
}
.secondary-btn:hover {
  box-shadow: 0 1px 2px 0 rgba(4, 26, 75, 0.17), 0 0 0 1px rgba(0, 56, 108, 0.12);
}

.loading-text-enter {
  animation: loadingTextEnter 400ms ease-in-out forwards;
}
@keyframes loadingTextEnter {
  0% { 
    height: 0px;
    opacity: 0; 
  }
  50% {
    height: 60px;
    opacity: 0;
  }
  100% { 
    height: 60px;
    opacity: 1; 
  }
}

.fade-in {
  animation: fadeIn 100ms ease-in-out;
}
.slow-fade-in {
  animation: fadeIn 200ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.v-enter-active, .v-leave-active {
  transition: opacity 100ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>